import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Apartment, UnfoldMore } from '@mui/icons-material';
import {
  CircularProgress,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  styled,
  Tooltip,
  Typography
} from '@mui/material';
import { useIsFetching } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { collapsibleItemSx } from '@/components/Layout/Sidebar';
import { SIDEBAR_COLORS } from '@/components/Layout/sidebarTheme';
import { getCareSettingTerminologyForFacilityTypes } from '@/hooks/facility-selection/careSettingTerminology';
import {
  getAllowedFacilityTypesForRoute,
  getFacilityTypeGroupOptions,
  getGenericAllLabelForType
} from '@/hooks/facility-selection/screenerFacilityTypeHelpers';
import { useFacilitySelectorController } from '@/hooks/facility-selection/useFacilitySelectorController';
import { useScreenerFacilityView } from '@/hooks/facility-selection/useScreenerFacilityView';
import {
  getScreenerSettingValue,
  ScreenerSettingName
} from '@/hooks/screener-settings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  ALL_FACILITIES,
  selectedFacilityIdAtom,
  useFacilitiesQuery
} from '@/hooks/useFacilitiesQuery';
import useIsScreenerAllFacilitiesSelected from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useIsScreenerAllFacilitiesSelected';

import FacilitySelectorPopover from './FacilitySelectorPopover';
import { getUserFacility } from './getUserFacilityHelper';

const StyledFacilitySelectorButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'expanded' && prop !== 'isOpen'
})<{ expanded: boolean; isOpen: boolean }>(({ theme, expanded, isOpen }) => ({
  width: expanded ? '100%' : 48,
  height: 48,
  flex: expanded ? undefined : 'none',
  gap: 8,
  padding: expanded ? '6px 16px' : 0,
  justifyContent: expanded ? 'flex-start' : 'center',
  background: theme.palette.neutral.white,
  border: `1px solid ${isOpen ? theme.palette.primary.main : theme.palette.neutral.border}`,
  borderRadius: 999,
  '&:hover': {
    background: theme.palette.neutral.white,
    borderColor: theme.palette.primary.main
  },
  '&.Mui-disabled': { opacity: 1 },
  '& .MuiSvgIcon-root': { fontSize: 20, color: theme.palette.text.secondary },
  '& .MuiListItemText-root': { margin: 0, minWidth: 0 },
  '& .MuiTypography-root': {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}));

const FacilitySelector = ({ expanded }: { expanded: boolean }) => {
  const { view, selectedFacilityId } = useScreenerFacilityView();
  const { selectFacility } = useFacilitySelectorController();
  const [, setSelectedFacilityId] = useAtom(selectedFacilityIdAtom);
  const isAllFacilitiesSelected = useIsScreenerAllFacilitiesSelected();

  const location = useLocation();
  const isCRM = location.pathname.startsWith('/crm');
  const allowedFacilityTypes = useMemo(
    () => getAllowedFacilityTypesForRoute(location.pathname),
    [location.pathname]
  );
  const currentUser = useCurrentUser().data!;
  const { data: facilitiesResponse, isLoading: isLoadingFacilitiesResponse } =
    useFacilitiesQuery().useFindAllWithCanSelectAllFlag();
  const ref = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const hasMultipleFacilities = currentUser.user_facility_accesses!.length > 1;

  const facilityIds = useMemo(
    () =>
      isCRM ? (facilitiesResponse?.facilities.map((f) => f.id) ?? []) : [],
    [facilitiesResponse, isCRM]
  );

  const billingDisabledFacilityIds = useMemo(() => {
    const ids = new Set<string>();
    for (const id of facilityIds) {
      if (
        getScreenerSettingValue(
          ScreenerSettingName.BillingScreenerEnabled,
          id
        ) === 'disabled'
      ) {
        ids.add(id);
      }
    }
    return ids;
  }, [facilityIds]);

  const anyFacilityBillingDisabled = billingDisabledFacilityIds.size > 0;

  const isFetching = useIsFetching() > 0;
  const [switchState, setSwitchState] = useState<
    'idle' | 'pending' | 'fetching'
  >('idle');

  useEffect(() => {
    if (switchState === 'pending') {
      setSwitchState(isFetching ? 'fetching' : 'idle');
    } else if (switchState === 'fetching' && !isFetching) {
      setSwitchState('idle');
    }
  }, [switchState, isFetching]);

  const onSelectFacility = useCallback(
    (facilityId: string) => {
      if (facilityId !== selectedFacilityId) {
        setSwitchState('pending');
      }
      selectFacility(facilityId);
      setIsOpen(false);
    },
    [selectFacility, selectedFacilityId]
  );

  const isAllFacilities =
    (!isCRM && selectedFacilityId === ALL_FACILITIES) ||
    isAllFacilitiesSelected;

  const userFacility = useMemo(() => {
    return getUserFacility(
      selectedFacilityId,
      facilitiesResponse,
      currentUser,
      setSelectedFacilityId,
      isAllFacilities
    );
  }, [
    selectedFacilityId,
    setSelectedFacilityId,
    facilitiesResponse,
    currentUser,
    isAllFacilities
  ]);

  const displayFacility =
    userFacility ??
    currentUser.user_facility_accesses?.find(
      (access) => access.facility_id === selectedFacilityId
    )?.facility;
  const isLoading = isLoadingFacilitiesResponse || switchState !== 'idle';

  const accessibleFacilityIds = useMemo(
    () =>
      new Set(
        currentUser.user_facility_accesses?.map((a) => a.facility_id) ?? []
      ),
    [currentUser.user_facility_accesses]
  );
  const groupOptions = useMemo(
    () =>
      getFacilityTypeGroupOptions(
        facilitiesResponse?.facilities ?? [],
        accessibleFacilityIds
      ),
    [facilitiesResponse?.facilities, accessibleFacilityIds]
  );
  const careSettingTerms = useMemo(
    () =>
      getCareSettingTerminologyForFacilityTypes(
        groupOptions.map((option) => option.facilityType)
      ),
    [groupOptions]
  );

  const activeGroup =
    view.kind === 'all-facilities'
      ? groupOptions.find((o) => o.facilityType === view.facilityType)
      : null;

  const isMultiType = groupOptions.length > 1;
  const allFacilitiesLabel =
    view.kind === 'all-facilities'
      ? isMultiType
        ? (activeGroup?.label ?? getGenericAllLabelForType(view.facilityType))
        : getGenericAllLabelForType(view.facilityType)
      : `All ${careSettingTerms.pluralTitle}`;

  const facilityCount = isAllFacilities
    ? activeGroup?.facilityIds.length
    : undefined;

  const facilityCountLabel =
    facilityCount !== undefined && facilityCount > 99 ? '99+' : facilityCount;

  return (
    <>
      <Tooltip
        title={
          expanded
            ? ''
            : isAllFacilities
              ? allFacilitiesLabel
              : (displayFacility?.name ?? '')
        }
        placement="right"
        disableInteractive
      >
        <span
          style={{
            display: 'inline-flex',
            width: '100%',
            justifyContent: 'center'
          }}
        >
          <StyledFacilitySelectorButton
            data-testid="community-selector"
            ref={ref}
            expanded={expanded}
            isOpen={isOpen}
            disabled={!hasMultipleFacilities || isLoadingFacilitiesResponse}
            onClick={() => hasMultipleFacilities && setIsOpen(true)}
          >
            <ListItemIcon sx={collapsibleItemSx}>
              <Stack alignItems="center">
                {isLoading ? <CircularProgress size={20} /> : <Apartment />}
                {!expanded && facilityCountLabel !== undefined && (
                  <Typography
                    sx={{
                      fontSize: '10px',
                      lineHeight: '14px',
                      fontWeight: 500,
                      color: SIDEBAR_COLORS.textSecondary
                    }}
                  >
                    {facilityCountLabel}
                  </Typography>
                )}
              </Stack>
            </ListItemIcon>
            {expanded && (
              <>
                <ListItemText
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: '20px',
                      color: 'text.secondary'
                    },
                    '& .MuiListItemText-secondary': {
                      fontSize: 12,
                      lineHeight: '16px',
                      color: 'neutral.caption'
                    }
                  }}
                  primary={
                    isAllFacilities
                      ? allFacilitiesLabel
                      : (displayFacility?.name ?? 'Loading…')
                  }
                  secondary={!isAllFacilities && displayFacility?.address}
                />
                {hasMultipleFacilities && (
                  <Stack
                    sx={{ width: 32, height: 32, flexShrink: 0, ml: 'auto' }}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <UnfoldMore sx={{ color: 'neutral.caption' }} />
                  </Stack>
                )}
              </>
            )}
          </StyledFacilitySelectorButton>
        </span>
      </Tooltip>
      <FacilitySelectorPopover
        user={currentUser}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        anchorEl={ref.current!}
        onSelectFacility={onSelectFacility}
        selectedFacilityId={selectedFacilityId}
        isCRM={isCRM}
        canSelectAllFacilities={
          facilitiesResponse?.can_select_all_facilities || false
        }
        billingDisabledFacilityIds={billingDisabledFacilityIds}
        anyFacilityBillingDisabled={anyFacilityBillingDisabled}
        facilities={facilitiesResponse?.facilities ?? []}
        accessibleFacilityIds={accessibleFacilityIds}
        allowedFacilityTypes={allowedFacilityTypes}
      />
    </>
  );
};

export default FacilitySelector;
